// AcademicsManager.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import HtmlEditor from "../pages/HtmlEditor";

/**
 * Props:
 *  - apiKey (string)   : required (sent as X-API-Key)
 *  - baseURL (string)  : API root; default: https://rankridgeschools.com/rankridgeschool/dev/v1
 */
export default function AcademicsManager({
  apiKey="12345678",
  baseURL = "https://gantasoft.in/rankridgeschool/dev/v1",
}) {
  const api = useMemo(
    () =>
      axios.create({
        baseURL,
        headers: { "X-API-Key": apiKey },
      }),
    [apiKey, baseURL]
  );

  // list state
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // filters / paging
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [pageId, setPageId] = useState("");
  const [subpageId, setSubpageId] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(12);

  // lookups
  const [pagesList, setPagesList] = useState([]);       // [{page_id,page_name,...}]
  const [subpagesList, setSubpagesList] = useState([]); // [{subpage_id,subpage_name,page_id,...}]

  // modal + form
  const [editing, setEditing] = useState(null); // null => add; object => edit
  const [saving, setSaving] = useState(false);

  // upload
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [desiredName, setDesiredName] = useState(""); // custom filename (no ext)

  /* ================== LOADERS ================== */
  async function loadList() {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/academics", {
        params: {
          search,
          status,
          page_id: pageId || undefined,
          subpage_id: subpageId || undefined,
          page,
          limit,
          sort: "sort_order",
          order: "asc",
        },
      });
      if (data?.success) {
        setItems(data.items || []);
        setTotal(data.pagination?.total || 0);
      } else {
        setErr(data?.message || "Failed to load");
      }
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || "Load error");
    } finally {
      setLoading(false);
    }
  }

  async function loadPages() {
    try {
      const { data } = await api.get("/pages", {
        params: { status: "active", sort: "page_name", order: "asc", limit: 500 },
      });
      setPagesList(data?.items || []);
    } catch {
      setPagesList([]);
    }
  }

  async function loadSubpagesByPage(pid) {
    if (!pid) {
      setSubpagesList([]);
      return;
    }
    try {
      const { data } = await api.get("/subpages", {
        params: { page_id: pid, status: "active", sort: "subpage_name", order: "asc", limit: 1000 },
      });
      setSubpagesList(data?.items || []);
    } catch {
      setSubpagesList([]);
    }
  }

  useEffect(() => { loadList(); }, [search, status, pageId, subpageId, page, limit]); // eslint-disable-line
  useEffect(() => { loadPages(); }, []); // preload pages list once

  const pagesCount = Math.max(1, Math.ceil(total / limit));

  /* ================== CRUD ================== */
  function openAdd() {
    setEditing({
      page_id: "",
      subpage_id: "",
      program_title: "",
      program_subtitle: "",
      program_name: "",
      program_description: "",
      image_url: "",
      sort_order: 0,
      status: "active",
    });
    setDesiredName("");
    setSubpagesList([]);
  }

  function openEdit(item) {
    setEditing({ ...item });
    setDesiredName("");
    // make sure lists are available
    loadPages().then(() => {
      if (item.page_id) loadSubpagesByPage(item.page_id);
      else setSubpagesList([]);
    });
  }

  // when modal's page_id changes, update subpages and clear subpage if needed
  useEffect(() => {
    if (!editing) return;
    const pid = editing.page_id ? Number(editing.page_id) : null;
    loadSubpagesByPage(pid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing?.page_id]);

  async function uploadImage() {
    const f = fileRef.current?.files?.[0];
    if (!f) return alert("Choose a file");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      if (desiredName.trim()) fd.append("name", desiredName.trim()); // custom base name
      // optional structured dir by page/subpage/category
      if (editing?.page_id) fd.append("dir", `academics/${editing.page_id}`);
      const { data } = await api.post("/upload", fd);
      if (data?.success && data?.path) {
        setEditing(s => ({ ...s, image_url: data.path }));
      } else {
        alert(data?.message || "Upload failed");
      }
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Upload error");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function save() {
    if (!editing) return;
    const payload = {
      page_id: editing.page_id ? Number(editing.page_id) : null,
      subpage_id: editing.subpage_id ? Number(editing.subpage_id) : null,
      program_title: editing.program_title,
      program_subtitle: editing.program_subtitle || null,
      program_name: editing.program_name,
      program_description: editing.program_description,
      image_url: editing.image_url || null,
      sort_order: Number(editing.sort_order) || 0,
      status: editing.status || "active",
    };
    if (!payload.program_title || !payload.program_name || !payload.program_description) {
      alert("Please fill program_title, program_name, and program_description.");
      return;
    }
    setSaving(true);
    try {
      if (editing.academic_id) {
        await api.put(`/academics/${editing.academic_id}`, payload);
      } else {
        await api.post(`/academics`, payload, { headers: { "Content-Type": "application/json" } });
      }
      setEditing(null);
      loadList();
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function destroy(id) {
    if (!window.confirm("Delete this academic program? This cannot be undone.")) return;
    try {
      await api.delete(`/academics/${id}`);
      setItems(prev => prev.filter(x => x.academic_id !== id));
      setTotal(t => Math.max(0, t - 1));
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Delete failed");
    }
  }

  /* ================== PATCH helpers ================== */
  async function patchStatus(id, current) {
    const next = current === "active" ? "inactive" : "active";
    try {
      await api.patch(`/academics/${id}`, { field: "status", value: next });
      setItems(prev => prev.map(x => x.academic_id === id ? { ...x, status: next } : x));
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Update failed");
    }
  }

  async function patchSort(id, newSort) {
    try {
      await api.patch(`/academics/${id}`, { field: "sort_order", value: Number(newSort) || 0 });
      setItems(prev => prev.map(x => x.academic_id === id ? { ...x, sort_order: Number(newSort) || 0 } : x));
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Sort update failed");
    }
  }

  return (
    <div style={wrap}>
      <h2 style={{ margin: 0 }}>Academics</h2>

      {/* Filters */}
      <div style={toolbar}>
        <input
          placeholder="Search title/name/description"
          value={search}
          onChange={(e)=>{ setPage(1); setSearch(e.target.value); }}
          style={input}
        />
        <select value={status} onChange={(e)=>{ setPage(1); setStatus(e.target.value); }} style={input}>
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="deleted">Deleted</option>
        </select>
        <input placeholder="Page ID" value={pageId} onChange={(e)=>{ setPage(1); setPageId(e.target.value); }} style={{...input, width:110}}/>
        <input placeholder="Subpage ID" value={subpageId} onChange={(e)=>{ setPage(1); setSubpageId(e.target.value); }} style={{...input, width:130}}/>
        <select value={limit} onChange={(e)=>{ setPage(1); setLimit(Number(e.target.value)); }} style={input}>
          {[6,12,24,48].map(n => <option key={n} value={n}>{n}/page</option>)}
        </select>
        <button onClick={loadList} style={btn("#1f2937","#fff")}>Refresh</button>
        <button onClick={openAdd} style={btn("#16a34a","#fff")}>Add Program</button>
      </div>

      {/* Grid */}
      {loading ? (
        <p>Loading…</p>
      ) : err ? (
        <p style={{ color:"#dc2626" }}>{err}</p>
      ) : items.length === 0 ? (
        <div style={emptyBox}>No programs found.</div>
      ) : (
        <>
          <div style={grid}>
            {items.map((it) => (
              <article key={it.academic_id} style={card}>
                <div style={{ display:"flex", gap:12 }}>
                  <div style={thumb}>
                    {it.image_url ? (
                      <img src={it.image_url} alt={it.program_title} style={img} />
                    ) : (
                      <div style={noimg}>No image</div>
                    )}
                  </div>

                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                      <h3 style={{ margin:0, fontSize:16 }}>{it.program_title}</h3>
                      <span style={{ ...chip, background: it.status==='active'?'#dcfce7':'#fee2e2' }}>
                        {it.status}
                      </span>
                      <span style={chip}>Sort: {it.sort_order ?? 0}</span>
                    </div>

                    <div style={{ marginTop:4, fontSize:13, color:"#374151" }}>
                      <div><strong>Name:</strong> {it.program_name}</div>
                      {it.program_subtitle && <div style={{ color:"#6b7280" }}>{it.program_subtitle}</div>}
                      <div style={{ marginTop:6, color:"#6b7280", fontSize:12 }}>
                        {it.page_id ? <>page_id: {it.page_id} · </> : null}
                        {it.subpage_id ? <>subpage_id: {it.subpage_id} · </> : null}
                        Updated: {it.updated_date?.replace("T"," ").replace("Z","") || "-"}
                      </div>
                    </div>

                    <div style={{ marginTop:10, display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
                      <button onClick={() => openEdit(it)} style={btn("#2563eb","#fff")}>Edit</button>
                      <button onClick={() => destroy(it.academic_id)} style={btn("#ef4444","#fff")}>Delete</button>
                      <button onClick={() => patchStatus(it.academic_id, it.status)} style={btn("#6b7280","#fff")}>
                        {it.status === "active" ? "Deactivate" : "Activate"}
                      </button>

                      {/* inline sort patch */}
                      <span style={{ marginLeft:"auto", fontSize:12, color:"#374151" }}>Change sort:</span>
                      <input
                        type="number"
                        defaultValue={it.sort_order ?? 0}
                        onBlur={(e)=>patchSort(it.academic_id, e.target.value)}
                        style={{ ...input, width:90 }}
                        title="Blur to save"
                      />
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <div style={pager}>
            <button disabled={page<=1} onClick={()=>setPage(p=>p-1)} style={btn("#e5e7eb","#111")}>Prev</button>
            <span style={{ fontSize:13, color:"#374151" }}>Page {page} / {pagesCount} (total {total})</span>
            <button disabled={page>=pagesCount} onClick={()=>setPage(p=>p+1)} style={btn("#e5e7eb","#111")}>Next</button>
          </div>
        </>
      )}

      {/* Add/Edit Modal */}
      {editing && (
        <div style={modalBackdrop} onClick={()=>setEditing(null)}>
          <div style={modal} onClick={(e)=>e.stopPropagation()}>
            <h3 style={{ marginTop:0 }}>{editing.academic_id ? "Edit Program" : "Add Program"}</h3>

            <div style={{ maxHeight:"65vh", overflowY:"auto", paddingRight:8, display:"grid", gap:10 }}>
              {/* Page/Subpage selects (names shown, IDs stored) */}
              <div style={{ display:"grid", gap:10, gridTemplateColumns:"repeat(auto-fit, minmax(220px, 1fr))" }}>
                <label style={lbl}>Page
                  <select
                    value={editing.page_id || ""}
                    onChange={(e) => {
                      const newPid = e.target.value;
                      setEditing(s => ({ ...s, page_id: newPid, subpage_id: "" }));
                      loadSubpagesByPage(newPid ? Number(newPid) : null);
                    }}
                    style={input}
                  >
                    <option value="">— None —</option>
                    {pagesList.map(p => (
                      <option key={p.page_id} value={p.page_id}>{p.page_name}</option>
                    ))}
                  </select>
                </label>

                <label style={lbl}>Subpage
                  <select
                    value={editing.subpage_id || ""}
                    onChange={(e) => setEditing(s => ({ ...s, subpage_id: e.target.value }))}
                    style={input}
                    disabled={!editing.page_id}
                  >
                    <option value="">— None —</option>
                    {subpagesList.map(sp => (
                      <option key={sp.subpage_id} value={sp.subpage_id}>{sp.subpage_name}</option>
                    ))}
                  </select>
                </label>
              </div>

              <label style={lbl}>Program Title
                <input value={editing.program_title || ""} onChange={(e)=>setEditing(s=>({...s, program_title:e.target.value}))} style={input}/>
              </label>
              <label style={lbl}>Program Subtitle
                <input value={editing.program_subtitle || ""} onChange={(e)=>setEditing(s=>({...s, program_subtitle:e.target.value}))} style={input}/>
              </label>
              <label style={lbl}>Program Name
                <input value={editing.program_name || ""} onChange={(e)=>setEditing(s=>({...s, program_name:e.target.value}))} style={input}/>
              </label>
              <label style={lbl}>Description
                {/* <textarea value={editing.program_description || ""} onChange={(e)=>setEditing(s=>({...s, program_description:e.target.value}))} style={{ ...input, minHeight: 120 }}/> */}
              <HtmlEditor
  value={editing.program_description || ""}
  onChange={(html) =>
    setEditing((s) => ({ ...s, program_description: html }))
  }
  minHeight={120}
/>

              </label>

              <label style={lbl}>Image URL
                <input value={editing.image_url || ""} onChange={(e)=>setEditing(s=>({...s, image_url:e.target.value}))} style={input}/>
              </label>
              <div style={{ display:"grid", gap:8 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                  <input ref={fileRef} type="file" accept="image/*,.svg,.webp,.ico" />
                  <button onClick={uploadImage} disabled={uploading} style={btn("#059669","#fff")}>
                    {uploading ? "Uploading…" : "Upload"}
                  </button>
                </div>
                <label style={lbl}>Desired File Name (no extension)
                  <input placeholder="e.g. pre-primary-alpha-edge" value={desiredName} onChange={(e)=>setDesiredName(e.target.value)} style={input}/>
                </label>
              </div>

              <div style={{ display:"grid", gap:10, gridTemplateColumns:"repeat(auto-fit, minmax(160px, 1fr))" }}>
                <label style={lbl}>Sort Order
                  <input type="number" value={editing.sort_order ?? 0} onChange={(e)=>setEditing(s=>({...s, sort_order: e.target.value }))} style={input}/>
                </label>
                <label style={lbl}>Status
                  <select value={editing.status || "active"} onChange={(e)=>setEditing(s=>({...s, status:e.target.value}))} style={input}>
                    <option value="active">active</option>
                    <option value="inactive">inactive</option>
                    <option value="deleted">deleted</option>
                  </select>
                </label>
              </div>

              {editing.image_url && (
                <div style={{ marginTop:8 }}>
                  <strong>Preview:</strong>
                  <div style={{ marginTop:8, border:"1px solid #e5e7eb", borderRadius:8, padding:8, background:"#fafafa" }}>
                    <img src={editing.image_url} alt={editing.program_title || editing.program_name} style={{ maxWidth:"100%", maxHeight:300, display:"block", margin:"0 auto" }}/>
                  </div>
                </div>
              )}
            </div>

            <div style={{ display:"flex", gap:8, marginTop:14, justifyContent:"flex-end" }}>
              <button onClick={save} disabled={saving} style={btn("#16a34a","#fff")}>
                {saving ? "Saving…" : "Save"}
              </button>
              <button onClick={()=>setEditing(null)} style={btn("#e5e7eb","#111")}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* styles */
const wrap = { maxWidth: 1200, margin:"0 auto", padding:16 };
const toolbar = { display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", margin:"12px 0 16px" };
const grid = { display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(360px, 1fr))", gap:12 };
const card = { border:"1px solid #e5e7eb", borderRadius:14, padding:14, background:"#fff" };
const input = { padding:"9px 12px", border:"1px solid #e5e7eb", borderRadius:10, minWidth:160 };
const btn = (bg, color) => ({ padding:"8px 12px", background:bg, color, border:"none", borderRadius:10, cursor:"pointer" });
const emptyBox = { border:"1px dashed #d1d5db", padding:24, borderRadius:12, color:"#6b7280", textAlign:"center" };
const pager = { display:"flex", alignItems:"center", gap:10, marginTop:16, justifyContent:"center" };
const thumb = { width:120, height:90, borderRadius:10, overflow:"hidden", background:"#f3f4f6", flexShrink:0, border:"1px solid #eef2f7" };
const img = { width:"100%", height:"100%", objectFit:"cover" };
const noimg = { fontSize:12, color:"#6b7280", display:"flex", alignItems:"center", justifyContent:"center", height:"100%", padding:8, textAlign:"center" };
const chip = { fontSize:11, background:"#eef2ff", color:"#3730a3", padding:"2px 6px", borderRadius:999 };
const modalBackdrop = { position:"fixed", inset:0, background:"rgba(0,0,0,.35)", display:"flex", alignItems:"center", justifyContent:"center", padding:16, zIndex:50 };
const modal = { width:"min(780px, 100%)", maxHeight:"90vh", overflowY:"auto", background:"#fff", borderRadius:14, padding:16, boxShadow:"0 10px 30px rgba(0,0,0,.2)" };
const lbl = { display:"grid", gap:6, fontSize:13, color:"#374151" };
